import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatDate } from "@/shared/lib/date-time";
import { AreaChart, type AreaChartData } from "@/shared/ui/AreaChart";
import { BarChart } from "@/shared/ui/BarChart";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { ChartConfig } from "@/shared/ui/ChartContainer";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { useSearch } from "@tanstack/react-router";
import { format, subDays } from "date-fns";
import { ClockIcon, DoorOpenIcon, DownloadIcon, TimerIcon } from "lucide-react";
import { useCallback, useMemo } from "react";
import {
  useRoomUsageByRoomQuery,
  useRoomUsagePeakQuery,
  useRoomUsageSummaryQuery,
  useRoomUsageTrendQuery,
} from "../api/queries";
import {
  RoomUsageReportFilter,
  type RoomUsageReportSearch,
} from "../ui/RoomUsageReportFilter";
import { RoomUsageReportTable } from "../ui/RoomUsageReportTable";
import { RoomUsageTrendTable } from "../ui/RoomUsageTrendTable";
import {
  ReportSummaryStatCard,
  ReportSummaryStatGrid,
  reportSummaryCount,
} from "../ui/ReportSummaryStatCard";

function useReportQuery(search: RoomUsageReportSearch) {
  const defaultFrom = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");
  const dateFrom = search.dateFrom ?? defaultFrom;
  const dateTo = search.dateTo ?? defaultTo;

  const summaryQuery = useMemo(
    () => ({
      dateFrom,
      dateTo,
      roomId: search.roomId,
    }),
    [dateFrom, dateTo, search.roomId],
  );

  return { dateFrom, dateTo, summaryQuery };
}

function escapeCsvCell(value: string | number): string {
  const s = String(value);
  if (
    s.includes(",") ||
    s.includes('"') ||
    s.includes("\n") ||
    s.includes("\r")
  ) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function RoomUsageReportPage() {
  const search = useSearch({
    from: "/app/reports/room-usage",
  }) as RoomUsageReportSearch;

  const { dateFrom, dateTo, summaryQuery } = useReportQuery(search);

  const { data: summary, isLoading: summaryLoading } =
    useRoomUsageSummaryQuery(summaryQuery);
  const { data: byRoom = [], isLoading: byRoomLoading } =
    useRoomUsageByRoomQuery(summaryQuery);
  const { data: trend = [], isLoading: trendLoading } =
    useRoomUsageTrendQuery(summaryQuery);
  const { data: peak = [] } = useRoomUsagePeakQuery(summaryQuery);

  const peakTimeLabel = useMemo(() => {
    if (!summary?.peakTimeStart || !summary?.peakTimeEnd) return "—";
    return `${summary.peakTimeStart}–${summary.peakTimeEnd}`;
  }, [summary?.peakTimeStart, summary?.peakTimeEnd]);

  const exportCsv = useCallback(() => {
    const header = "ວັນທີ,ຊົ່ວໂມງທີ່ໃຊ້,ຈຳນວນຄັ້ງ";
    const rows = trend.map(
      (t) =>
        `${escapeCsvCell(formatDate(t.date))},${escapeCsvCell(t.totalHoursUsed)},${escapeCsvCell(t.sessionsCount)}`,
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `room-usage-report-${dateFrom}-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [trend, dateFrom, dateTo]);

  const areaChartData: AreaChartData[] = useMemo(
    () =>
      trend.map((t) => ({
        date: formatDate(t.date),
        totalHoursUsed: t.totalHoursUsed,
        sessionsCount: t.sessionsCount,
      })),
    [trend],
  );

  const areaChartConfig: ChartConfig = {
    totalHoursUsed: {
      label: "ຊົ່ວໂມງທີ່ໃຊ້",
      color: "var(--chart-1)",
    },
    sessionsCount: {
      label: "ຈຳນວນຄັ້ງ",
      color: "var(--chart-2)",
    },
  };

  const byRoomBarData = useMemo(
    () =>
      byRoom.map((r) => ({
        room: r.roomName,
        hoursUsed: r.hoursUsed,
      })),
    [byRoom],
  );

  const byRoomBarConfig: ChartConfig = {
    hoursUsed: {
      label: "ຊົ່ວໂມງ",
      color: "var(--chart-1)",
    },
  };

  const peakBarData = useMemo(
    () => peak.map((p) => ({ hourSlot: p.hourSlot, count: p.count })),
    [peak],
  );

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ລາຍງານການໃຊ້ຫ້ອງ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl">ລາຍງານການໃຊ້ຫ້ອງ</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ສະແດງຊົ່ວໂມງທີ່ໃຊ້ ຈຳນວນຄັ້ງ ແລະເວລາເຄັບ
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={trend.length === 0}
          >
            <DownloadIcon className="mr-2 size-4" />
            ດາວໂຫລດ CSV
          </Button>
        </div>

        <div className="mb-6 flex flex-col rounded-xl border bg-card pt-2">
          <RoomUsageReportFilter />
        </div>

        <ReportSummaryStatGrid columns={3}>
          <ReportSummaryStatCard
            title="ຊົ່ວໂມງທີ່ໃຊ້ທັງໝົດ"
            icon={TimerIcon}
            tone="attendance"
            isLoading={summaryLoading}
            value={
              <>
                {reportSummaryCount(summary?.totalHoursUsed ?? 0)}{" "}
                <span className="font-normal text-muted-foreground text-sm">
                  ຊົ່ວໂມງ
                </span>
              </>
            }
          />
          <ReportSummaryStatCard
            title="ຈຳນວນຄັ້ງ"
            icon={ClockIcon}
            tone="packages"
            isLoading={summaryLoading}
            value={reportSummaryCount(summary?.sessionsCount ?? 0)}
          />
          <ReportSummaryStatCard
            title="ເວລາເຄັບ"
            icon={DoorOpenIcon}
            tone="students"
            isLoading={summaryLoading}
            value={peakTimeLabel}
          />
        </ReportSummaryStatGrid>

        {areaChartData.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>ແນວໂນ້ມການໃຊ້ຫ້ອງ</CardTitle>
              <p className="text-muted-foreground text-sm">
                ຊົ່ວໂມງທີ່ໃຊ້ແລະຈຳນວນຄັ້ງ ຕາມວັນທີ
              </p>
            </CardHeader>
            <CardContent>
              <AreaChart
                data={areaChartData}
                config={areaChartConfig}
                xDataKey="date"
                valueDataKey={["totalHoursUsed", "sessionsCount"]}
                xAxisTickFormatter={(v) =>
                  typeof v === "string" ? v.slice(0, 10) : String(v)
                }
                className="mx-auto aspect-video max-h-[300px]"
              />
            </CardContent>
          </Card>
        )}

        <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {byRoomBarData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>ຊົ່ວໂມງທີ່ໃຊ້ແຍກຕາມຫ້ອງ</CardTitle>
                <p className="text-muted-foreground text-sm">
                  ສະແດງຊົ່ວໂມງທີ່ໃຊ້ແຕ່ລະຫ້ອງ
                </p>
              </CardHeader>
              <CardContent>
                <BarChart
                  data={byRoomBarData}
                  config={byRoomBarConfig}
                  xDataKey="room"
                  valueDataKey="hoursUsed"
                  xAxisTickFormatter={(v) =>
                    typeof v === "string" && v.length > 12
                      ? `${v.slice(0, 10)}...`
                      : String(v)
                  }
                  className="mx-auto aspect-video max-h-[280px]"
                />
              </CardContent>
            </Card>
          )}

          {peakBarData.some((p) => p.count > 0) && (
            <Card>
              <CardHeader>
                <CardTitle>ຈຳນວນຄັ້ງ ຕໍ່ຊ່ວງເວລາ</CardTitle>
                <p className="text-muted-foreground text-sm">
                  ເວລາເຄັບ — ຈຳນວນຄັ້ງ ໃນແຕ່ລະຊ່ວງ 3 ຊົ່ວໂມງ
                </p>
              </CardHeader>
              <CardContent>
                <BarChart
                  data={peakBarData}
                  config={{
                    count: { label: "ຈຳນວນຄັ້ງ", color: "var(--chart-2)" },
                  }}
                  xDataKey="hourSlot"
                  valueDataKey="count"
                  className="mx-auto aspect-video max-h-[280px]"
                />
              </CardContent>
            </Card>
          )}
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>ຕາຕະລາງລາຍວັນ</CardTitle>
              <p className="text-muted-foreground text-sm">
                ຊົ່ວໂມງທີ່ໃຊ້ແລະຈຳນວນຄັ້ງ ຕາມວັນທີ
              </p>
            </CardHeader>
            <CardContent>
              <RoomUsageTrendTable isLoading={trendLoading} data={trend} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>ຕາຕະລາງແຍກຕາມຫ້ອງ</CardTitle>
              <p className="text-muted-foreground text-sm">
                ຊົ່ວໂມງທີ່ໃຊ້ແລະຈຳນວນຄັ້ງ ແຕ່ລະຫ້ອງ
              </p>
            </CardHeader>
            <CardContent>
              <RoomUsageReportTable isLoading={byRoomLoading} data={byRoom} />
            </CardContent>
          </Card>
        </div>
      </Main>
    </>
  );
}
